'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCategoryTreeSource,
  useBlockPart,
  type CategoryTreeNode,
  type CategoryTreeType,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import { SectionShell } from '../_shared/SectionShell';
import { BlockRoot } from '../_shared/BlockRoot';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';

/**
 * Reusable hierarchical category tree. The taxonomy is selected in the
 * admin via {@code feedSource} (courses / blog / activities / …), so
 * the same block drops onto the blog index, the activities index, the
 * catalog, etc. — only the source dropdown changes. Data comes from
 * the host's {@link CategoryTreeSource} adapter
 * ({@code cms/category/tree/{type}}).
 *
 * <p>Two layouts:
 * <ul>
 *   <li>{@code tree} — AntD-style collapsible sidebar menu. Parent
 *       categories expand/collapse inline (rotating caret); the whole
 *       panel collapses under its header. Best as a left filter rail.</li>
 *   <li>{@code columns} — top-level categories as a responsive grid,
 *       each listing its descendants down to {@code maxDepth}. Best as
 *       a flat "browse" footer/section.</li>
 * </ul>
 *
 * <p>{@code selectionMode} controls what a row does:
 * <ul>
 *   <li>{@code filter} — each row is a checkbox; toggling writes the
 *       selected ids to the URL ({@code ?category=id1,id2}), preserving
 *       other query params. Drives a server/client-filtered list.</li>
 *   <li>{@code link} — each row navigates to {@code hrefTemplate}.</li>
 * </ul>
 */
export type CategoryTreeFeedSource = CategoryTreeType;

export interface CategoryTreeProps {
  title?: string;
  /** Which taxonomy to render — maps to the {@code cms/category/tree/{type}}
   *  endpoint segment. Default {@code course}. */
  feedSource?: CategoryTreeFeedSource;
  /** Escape hatch for a content surface not in the dropdown. When set,
   *  overrides {@code feedSource}. */
  treeType?: string;
  /** How deep to descend. Default 3. */
  maxDepth?: number;
  layout?: 'tree' | 'columns';
  /** What a row does. Default {@code filter}. */
  selectionMode?: 'filter' | 'link';
  /** {@code filter} mode: the URL query param that holds the selected
   *  ids (comma-separated). Default {@code category}. */
  queryParam?: string;
  /** {@code link} mode: link target per category. {@code {id}} and
   *  {@code {slug}} are replaced. Default {@code ?category={id}}. */
  hrefTemplate?: string;
  /** Show each category's thumbnail/icon next to its name. Default false. */
  showImages?: boolean;
  /** Initial expand state for parent nodes. Default {@code none} — only
   *  top-level categories show; their children stay hidden until the row is
   *  expanded. {@code roots} pre-opens depth-1 nodes (their children show
   *  immediately); {@code all} opens the whole tree. */
  initiallyExpanded?: 'all' | 'roots' | 'none';
  /** Wrap the list under a collapsible panel header (the title). Default
   *  true (tree layout only). */
  collapsibleHeader?: boolean;
  /** Whether the panel header starts open. Default true. */
  headerInitiallyOpen?: boolean;
  /** Optional language hint passed to the adapter. */
  language?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function hasChildren(node: CategoryTreeNode): boolean {
  return Array.isArray(node.children) && node.children.length > 0;
}

function nodeId(node: CategoryTreeNode): string {
  return String(node.id);
}

function nodeHref(template: string, node: CategoryTreeNode): string {
  const id = nodeId(node);
  const slug = node.slug || id;
  return template
    .replace('{id}', encodeURIComponent(id))
    .replace('{slug}', encodeURIComponent(slug));
}

/** Build a query-string href that toggles {@code id} in the param,
 *  preserving every other param already on the URL. */
function toggledHref(
  param: string,
  currentSearch: string,
  selected: Set<string>,
  id: string,
): string {
  const sp = new URLSearchParams(currentSearch);
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  if (next.size > 0) sp.set(param, [...next].join(','));
  else sp.delete(param);
  const qs = sp.toString();
  return qs ? `?${qs}` : '?';
}

/** Build a query-string href that drops {@code param} entirely,
 *  preserving every other param already on the URL. */
function clearedHref(param: string, currentSearch: string): string {
  const sp = new URLSearchParams(currentSearch);
  sp.delete(param);
  const qs = sp.toString();
  return qs ? `?${qs}` : '?';
}

function defaultOpen(depth: number, mode: 'all' | 'roots' | 'none'): boolean {
  return mode === 'all' || (mode === 'roots' && depth === 1);
}

/** Default URL query param per feed source, used when the operator
 *  hasn't set an explicit {@code queryParam}. Languages filter by
 *  {@code lng}; everything else by {@code category}. */
const DEFAULT_QUERY_PARAM: Record<string, string> = {
  language: 'lng',
  channel: 'ct',
  level: 'level',
  registered: 'skipRegistered',
  demanded: 'demanded',
  activity_type: 'eventType',
  activity_educational: 'educationLevel',
  activity_categories: 'category',
  activity_provinces: 'city',
  'blog-tags': 'tag',
  document_category: 'categoryId',
  document_tags: 'tagId',
};

function defaultQueryParam(type: string): string {
  return DEFAULT_QUERY_PARAM[type] ?? 'category';
}

interface TreeCtx {
  selectionMode: 'filter' | 'link';
  queryParam: string;
  hrefTemplate: string;
  showImages: boolean;
  initiallyExpanded: 'all' | 'roots' | 'none';
  maxDepth: number;
  /** filter mode: ids currently selected (from the URL, post-mount). */
  selected: Set<string>;
  /** filter mode: the live URL query string (post-mount; '' before). */
  currentSearch: string;
}

export function CategoryTree({
  title,
  feedSource = 'course',
  treeType,
  maxDepth = 3,
  layout = 'tree',
  selectionMode = 'filter',
  queryParam,
  hrefTemplate = '?category={id}',
  showImages = false,
  initiallyExpanded = 'none',
  collapsibleHeader = true,
  headerInitiallyOpen = true,
  language,
  paddingOuter,
  paddingInner,
}: CategoryTreeProps) {
  const source = useCategoryTreeSource();
  const part = useBlockPart();
  const type = (treeType && treeType.trim()) || feedSource;
  const depth = clamp(maxDepth, 1, 6);
  // Explicit queryParam wins; otherwise derive from the feed source
  // (e.g. languages → ?lng=, categories → ?category=).
  const effectiveParam = (queryParam && queryParam.trim()) || defaultQueryParam(type);
  // Reactive URL query — re-renders on App Router navigation (including
  // next/link clicks), so checkbox state + toggled hrefs stay in sync.
  const searchParams = useSearchParams();
  const search = selectionMode === 'filter' ? (searchParams?.toString() ?? '') : '';

  const state = useAsyncResource<CategoryTreeNode[]>(async () => {
    if (!source) return null;
    const tree = await source.getTree(type, language ? { language } : undefined);
    return tree.length > 0 ? tree : null;
  }, [source, type, language]);

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);

  if (!source) {
    return (
      <SectionShell title={title} paddingOuter={paddingOuter} paddingInner={paddingInner}>
        <AdapterPlaceholder blockType="CategoryTree" adapterName="CategoryTreeSource" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') return null;

  const roots = state.data;

  if (layout === 'columns') {
    return (
      <SectionShell title={title} paddingOuter={paddingOuter} paddingInner={paddingInner}>
        <style dangerouslySetInnerHTML={{ __html: COLUMNS_CSS }} />
        <div className="sb-ct-columns" style={columnsGridStyle} {...part('list')}>
          {roots.map((root) => (
            <CategoryColumn
              key={root.id}
              node={root}
              template={hrefTemplate}
              depth={1}
              maxDepth={depth}
            />
          ))}
        </div>
      </SectionShell>
    );
  }

  const selected = selectionMode === 'filter' ? parseSelected(effectiveParam, search) : EMPTY_SET;
  const ctx: TreeCtx = {
    selectionMode,
    queryParam: effectiveParam,
    hrefTemplate,
    showImages,
    initiallyExpanded,
    maxDepth: depth,
    selected,
    currentSearch: search,
  };

  return (
    <BlockRoot as="div" style={{ ...panelStyle, ...outerPad }}>
      <div style={innerPad}>
        <style dangerouslySetInnerHTML={{ __html: TREE_CSS }} />
        <CategoryPanel
          title={title}
          roots={roots}
          ctx={ctx}
          collapsibleHeader={collapsibleHeader}
          headerInitiallyOpen={headerInitiallyOpen}
        />
      </div>
    </BlockRoot>
  );
}

const EMPTY_SET: Set<string> = new Set();

function parseSelected(param: string, search: string): Set<string> {
  if (!search) return EMPTY_SET;
  const raw = new URLSearchParams(search).get(param);
  return new Set(raw ? raw.split(',').filter(Boolean) : []);
}

// ── tree layout (AntD-style collapsible menu) ─────────────────────

const panelStyle: CSSProperties = {
  width: '100%',
  fontFamily: 'var(--font-family-base, inherit)',
};

function CategoryPanel({
  title,
  roots,
  ctx,
  collapsibleHeader,
  headerInitiallyOpen,
}: {
  title?: string;
  roots: CategoryTreeNode[];
  ctx: TreeCtx;
  collapsibleHeader: boolean;
  headerInitiallyOpen: boolean;
}) {
  const { t } = useTranslation('common');
  const part = useBlockPart();
  const [open, setOpen] = useState(headerInitiallyOpen);

  // Collapse by default on small viewports
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 900) {
      setOpen(false);
    }
  }, []);

  const list = (
    <ul style={menuListStyle} role="menu" {...part('list')}>
      {roots.map((node) => (
        <CategoryNode key={node.id} node={node} depth={1} ctx={ctx} />
      ))}
    </ul>
  );

  const hasSelection = ctx.selectionMode === 'filter' && ctx.selected.size > 0;
  const clearLink = hasSelection && (
    <Link
      href={clearedHref(ctx.queryParam, ctx.currentSearch)}
      scroll={false}
      style={clearLinkStyle}
      onClick={(e) => e.stopPropagation()}
      {...part('action')}
    >
      {t('search.clear')}
    </Link>
  );

  if (!collapsibleHeader) {
    return (
      <Card style={panelInnerStyle}>
        {title && (
          <div style={plainHeaderRowStyle}>
            <span>{title}</span>
            {clearLink}
          </div>
        )}
        {list}
      </Card>
    );
  }

  return (
    <Card style={panelInnerStyle}>
      <div style={panelHeaderRowStyle}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={panelHeaderStyle}
        >
          <Chevron open={open} />
          <span style={panelHeaderTextStyle}>{title || 'Kategoriler'}</span>
        </button>
        {clearLink}
      </div>
      {open && list}
    </Card>
  );
}

function CategoryNode({
  node,
  depth,
  ctx,
}: {
  node: CategoryTreeNode;
  depth: number;
  ctx: TreeCtx;
}) {
  const expandable = hasChildren(node) && depth < ctx.maxDepth;
  const [open, setOpen] = useState(() => defaultOpen(depth, ctx.initiallyExpanded));

  const id = nodeId(node);
  const selected = ctx.selectionMode === 'filter' && ctx.selected.has(id);

  const href =
    ctx.selectionMode === 'filter'
      ? toggledHref(ctx.queryParam, ctx.currentSearch, ctx.selected, id)
      : nodeHref(ctx.hrefTemplate, node);

  return (
    <li role="none" style={{ listStyle: 'none' }}>
      <div className="sb-ct-row" style={{ ...rowStyle, paddingLeft: 8 + (depth - 1) * 16 }}>
        <Link
          href={href}
          role={ctx.selectionMode === 'filter' ? 'checkbox' : undefined}
          aria-checked={ctx.selectionMode === 'filter' ? selected : undefined}
          scroll={false}
          style={rowLinkStyle}
        >
          {ctx.selectionMode === 'filter' && (
            <span className={`sb-ct-check ${selected ? 'sb-ct-checked' : ''}`} aria-hidden>
              {selected && <CheckIcon />}
            </span>
          )}
          {ctx.showImages && (node.thumbnailUrl || node.imageUrl) && (
            <span
              aria-hidden
              style={{
                ...thumbStyle,
                background: `url("${node.thumbnailUrl || node.imageUrl}") center/cover, var(--color-neutral-200, #e4e4e7)`,
              }}
            />
          )}
          <span
            className="sb-ct-label"
            title={node.name}
            style={{ fontWeight: selected ? 600 : depth === 1 ? 500 : 400 }}
          >
            {node.name}
          </span>
        </Link>
        {expandable && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Daralt' : 'Genişlet'}
            className="sb-ct-toggle"
            style={toggleBtnStyle}
          >
            <Chevron open={open} />
          </button>
        )}
      </div>
      {expandable && open && (
        <ul style={menuListStyle} role="menu">
          {node.children!.map((child) => (
            <CategoryNode key={child.id} node={child} depth={depth + 1} ctx={ctx} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`sb-ct-chevron ${open ? 'sb-ct-open' : ''}`}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const panelInnerStyle: CSSProperties = {
  ...cardReset,
  borderRadius: 'var(--radius-md, 12px)',
  background: 'var(--color-surface-card, #fff)',
  padding: 'var(--space-2, 8px)',
};

const panelHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const panelHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
  padding: '10px 8px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
};

const panelHeaderTextStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 15,
  color: 'var(--color-text-primary, #18181b)',
};

const plainHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  fontWeight: 600,
  fontSize: 15,
  color: 'var(--color-text-primary, #18181b)',
  padding: '10px 8px',
};

const clearLinkStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  color: 'var(--color-text-secondary, #71717a)',
  textDecoration: 'none',
  padding: '10px 8px',
  flexShrink: 0,
  cursor: 'pointer',
};

const menuListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 'var(--radius-sm, 8px)',
  paddingRight: 4,
};

const rowLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
  padding: '7px 6px',
  textDecoration: 'none',
  color: 'var(--color-text-primary, #18181b)',
  fontSize: 14,
};

const thumbStyle: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 'var(--radius-sm, 6px)',
  flexShrink: 0,
};

const toggleBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  flexShrink: 0,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--color-text-secondary, #71717a)',
};

const TREE_CSS = `
  .sb-ct-row { transition: background 120ms ease; }
  .sb-ct-row:hover { background: var(--color-neutral-100, #f4f4f5); }
  .sb-ct-label {
    flex: 1;
    min-width: 0;
    max-width: 210px;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sb-ct-chevron {
    flex-shrink: 0;
    color: var(--color-text-secondary, #71717a);
    transform: rotate(-90deg);
    transition: transform 180ms ease;
  }
  .sb-ct-chevron.sb-ct-open { transform: rotate(0deg); }
  .sb-ct-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border-radius: 4px;
    border: 1.5px solid var(--color-neutral-300, #d4d4d8);
    background: var(--color-surface-card, #fff);
    font-size: 11px;
    line-height: 1;
    color: #fff;
  }
  .sb-ct-check.sb-ct-checked {
    background: var(--color-brand-500, #3b5bdb);
    border-color: var(--color-brand-500, #3b5bdb);
  }
  @media(max-width:767px){
    .sb-ct-label { max-width: none; }
    .sb-ct-row { min-height: 44px; }
    .sb-ct-toggle { width: 44px!important; height: 44px!important; }
  }
`;

// ── columns layout ────────────────────────────────────────────────

const COLUMNS_CSS = `
  @media(max-width:767px){
    .sb-ct-columns { grid-template-columns: repeat(2, 1fr)!important; gap: 16px!important; }
  }
  @media(max-width:479px){
    .sb-ct-columns { grid-template-columns: 1fr!important; }
  }
`;

const columnsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 'var(--space-6, 24px)',
};

function CategoryColumn({
  node,
  template,
  depth,
  maxDepth,
}: {
  node: CategoryTreeNode;
  template: string;
  depth: number;
  maxDepth: number;
}) {
  return (
    <div>
      <Link
        href={nodeHref(template, node)}
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 'var(--font-tracking-wider, 0.06em)',
          color: 'var(--color-text-primary, #18181b)',
          textDecoration: 'none',
          marginBottom: 'var(--space-3, 12px)',
        }}
      >
        {node.name}
      </Link>
      {depth < maxDepth && hasChildren(node) && (
        <ChildList
          nodes={node.children!}
          template={template}
          depth={depth + 1}
          maxDepth={maxDepth}
        />
      )}
    </div>
  );
}

function ChildList({
  nodes,
  template,
  depth,
  maxDepth,
}: {
  nodes: CategoryTreeNode[];
  template: string;
  depth: number;
  maxDepth: number;
}) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: depth === 2 ? 0 : '4px 0 0' }}>
      {nodes.map((node) => (
        <li key={node.id} style={{ marginTop: depth === 2 ? 6 : 4, paddingLeft: (depth - 2) * 12 }}>
          <Link
            href={nodeHref(template, node)}
            style={{
              fontSize: depth === 2 ? 14 : 13,
              color: 'var(--color-text-secondary, #52525b)',
              textDecoration: 'none',
            }}
          >
            {node.name}
          </Link>
          {depth < maxDepth && hasChildren(node) && (
            <ChildList
              nodes={node.children!}
              template={template}
              depth={depth + 1}
              maxDepth={maxDepth}
            />
          )}
        </li>
      ))}
    </ul>
  );
}
